import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Flag, Wrench, LogOut, Activity, Gauge, CircleDot, Users, MapPin, Menu, CloudSun, Settings as SettingsIcon } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useSettings, fileUrl } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { LangSwitch } from "@/components/LangSwitch";
import { useWeatherStatus } from "@/components/weather/useWeatherStatus";
import { RainAlert } from "@/components/weather/RainAlert";

const rainLabel = (r, t) => (r.state === "raining" ? t("wx_raining") : t(r.state === "possible" ? "wx_rain_possible" : "wx_rain_in").replace("{m}", r.mins));

const WxBadge = ({ status, where }) => {
  const { t } = useT();
  if (!status || (!status.rain && !status.alerts)) return null;
  const title = [status.rain ? rainLabel(status.rain, t) : "", status.alerts ? `${status.alerts} ${t("wx_alerts_active")}` : ""].filter(Boolean).join(" · ");
  const bg = status.rain?.state === "possible" ? "bg-[#FF9500] text-[#0A0B10]" : "bg-[#FF3B30] text-white";
  return (
    <span data-testid={`${where}-weather-badge`} data-rain={status.rain ? status.rain.state : "false"} title={title}
      className={`relative inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full font-mono text-[10px] font-bold leading-none ${bg}`}>
      {status.rain && <span className={`absolute inset-0 rounded-full animate-ping opacity-60 ${bg}`} />}
      <span className="relative">{status.rain ? (status.rain.state === "raining" ? "!" : `${status.rain.state === "possible" ? "~" : ""}${status.rain.mins}'`) : status.alerts}</span>
    </span>
  );
};

const NAV = [
  ["/events", "events", Flag],
  ["/telemetry", "telemetry", Activity],
  ["/weather", "weather", CloudSun],
  ["/pressures", "pressures", Gauge],
  ["/tyres", "tyres", CircleDot],
  ["/drivers", "drivers", Users],
  ["/tracks", "tracks", MapPin],
  ["/garage", "garage", Wrench],
];

const navCls = ({ isActive }) =>
  `flex items-center gap-2 px-2.5 2xl:px-3 py-2 font-heading uppercase tracking-wide text-[15px] whitespace-nowrap transition-colors duration-200 border-b-2 ${
    isActive ? "text-white border-[#FF3B30]" : "text-slate-400 border-transparent hover:text-white"
  }`;

const drawerCls = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-3 rounded-sm font-heading uppercase tracking-wide text-lg transition-colors duration-200 border-l-2 ${
    isActive ? "text-white border-[#FF3B30] bg-[#FF3B30]/10" : "text-slate-400 border-transparent hover:text-white hover:bg-[#181B26]"
  }`;

const MobileNav = ({ t, userName, wx }) => {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <button type="button" data-testid="nav-menu-btn" onClick={() => setOpen(true)} title="Menu"
        className="relative xl:hidden p-2 rounded-sm text-slate-300 hover:text-white hover:bg-[#181B26] transition-colors duration-200">
        <Menu className="w-5 h-5" />
        {wx && (wx.rain || wx.alerts > 0) && <span data-testid="nav-menu-weather-dot" className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[#FF3B30]" />}
      </button>
      <SheetContent side="left" data-testid="nav-drawer" className="w-72 bg-[#0D0F15] border-[#282D3D] text-slate-200 p-4 flex flex-col gap-1">
        <SheetTitle className="font-heading uppercase text-sm tracking-[0.2em] text-slate-500 mb-3">Menu</SheetTitle>
        {NAV.map(([to, k, Icon]) => (
          <NavLink key={to} to={to} data-testid={`drawer-${k}-link`} className={drawerCls} onClick={() => setOpen(false)}>
            <Icon className="w-5 h-5" /> {t(`nav_${k}`)}{k === "weather" && <WxBadge status={wx} where="drawer" />}
          </NavLink>
        ))}
        <NavLink to="/settings" data-testid="drawer-settings-link" className={drawerCls} onClick={() => setOpen(false)}>
          <SettingsIcon className="w-5 h-5" /> {t("nav_settings")}
        </NavLink>
        <span data-testid="drawer-user-name" className="mt-auto px-4 pt-4 border-t border-[#282D3D] font-mono text-xs text-slate-500 truncate">{userName}</span>
      </SheetContent>
    </Sheet>
  );
};

const TeamBadge = ({ userName }) => {
  const { settings } = useSettings();
  return (
    <div data-testid="team-badge" className="hidden md:flex items-center gap-2.5 border-r border-[#282D3D] pr-3">
      {settings.team_logo && <img data-testid="team-logo" src={fileUrl(settings.team_logo.id)} alt="Team logo" className="h-9 w-auto max-w-[80px] object-contain" />}
      <div className="flex flex-col leading-none min-w-0">
        {settings.team_name && <span data-testid="team-name" className="font-heading uppercase font-bold text-base tracking-wide truncate max-w-[130px] 2xl:max-w-[200px]">{settings.team_name}</span>}
        <span data-testid="current-user-name" className={`font-mono text-slate-500 truncate max-w-[130px] 2xl:max-w-[200px] ${settings.team_name ? "text-[10px] mt-1" : "text-xs"}`}>{userName}</span>
      </div>
    </div>
  );
};

export const Layout = () => {
  const { user, logout } = useAuth();
  const { t, lang } = useT();
  const wx = useWeatherStatus(lang);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const onLogout = async () => {
    await logout();
    navigate("/login");
  };
  return (
    <div className="grain min-h-screen bg-[#090A0F] text-slate-200">
      <header className="sticky top-0 z-30 bg-[#090A0F]/85 backdrop-blur-xl border-b border-[#282D3D]">
        <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-3 sm:px-6 lg:px-8 h-16 flex items-center gap-2 sm:gap-4">
          <MobileNav t={t} userName={user?.name || user?.email} wx={wx} />
          <NavLink to="/events" data-testid="brand-link" className="flex items-center gap-2.5 mr-2 shrink-0">
            <span className="checker w-7 h-7 rounded-sm" />
            <span className="hidden sm:flex flex-col leading-none">
              <span className="font-heading font-extrabold uppercase text-lg leading-none tracking-tight">
                Kart Setup <span className="text-[#FF3B30]">&</span> Data Analisys
              </span>
              <span data-testid="brand-subtitle" className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400 mt-0.5">by RacingDreams</span>
            </span>
          </NavLink>
          <nav data-testid="main-nav" className="hidden xl:flex items-center gap-0.5 min-w-0">
            {NAV.map(([to, k, Icon]) => (
              <NavLink key={to} to={to} data-testid={`nav-${k}-link`} className={navCls}>
                <Icon className="w-4 h-4" /> {t(`nav_${k}`)}{k === "weather" && <WxBadge status={wx} where="nav" />}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-1 sm:gap-3 shrink-0">
            <div className="hidden min-[1700px]:block"><TeamBadge userName={user?.name || user?.email} /></div>
            <NavLink to="/settings" data-testid="nav-settings-link" title={t("nav_settings")}
              className={({ isActive }) => `p-2 rounded-sm transition-colors duration-200 ${isActive ? "text-[#FF3B30]" : "text-slate-400 hover:text-white hover:bg-[#181B26]"}`}>
              <SettingsIcon className="w-4 h-4" />
            </NavLink>
            <LangSwitch />
            <button
              data-testid="logout-btn"
              onClick={onLogout}
              title={t("logout")}
              className="p-2 rounded-sm text-slate-400 hover:text-white hover:bg-[#181B26] transition-colors duration-200"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>
      {wx?.rain && pathname !== "/weather" && (
        <div className="sticky top-16 z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-3">
          <RainAlert data={wx.data} place={wx.place} link />
        </div>
      )}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <Outlet />
      </main>
    </div>
  );
};
